import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { validateJmesPath } from 'hooks';
import { useForm } from '@weezevent/weezjs-core';
import useAppContext from 'context/useAppContext';
import { usePermissions } from '../../models/user/permissions';
import { Formatter as FormatterModel, Organization } from '../../models';

import { Section, SelectHierarchyLabel, CheckboxLabel, Input, Button, FrameCard, Icon, ButtonDropdown, ToastContext, WeezHeader, Message, Accordion } from '@weezevent/nacre';
import { JsonEditorField, InfoTextTooltip, LoadingCard, LoadingOptions } from '../../components';
import { FormatterActions, FormatterValues } from './components';

import css from './components/formatter.module.css';
import { filterEmptyValues } from 'utils/utils';
import { JSON_DRIVER } from './formatterConstants';

export const Formatter = ({ id = null, organizationId }) => {
    const history = useHistory();
    const { t } = useTranslation();
    const user = usePermissions();

    const toast = React.useContext(ToastContext);

    const [search, setSearch] = React.useState('');
    const [template, setTemplate] = React.useState(null);
    const [isOrigin, setIsOrigin] = React.useState(false);

    const canManageFormatters = user.canManageFormatters(organizationId);

    const [form, setForm, setFormObject] = useForm({
        organization_id: Number(organizationId),
        name: '',
        driver: JSON_DRIVER.raw,
        definition: {
            jmespath_filter: '',
            jmespath_values: {},
            template: ''
        }
    });

    const [formatter, loadingFormatter] = FormatterModel.useApiModel(
        {
            id,
            organizationId,
            strict: true,
            cache: false,
            allow_cache: false,
            launch: Boolean(id),
            onSuccess: response => {
                const definition = JSON.parse(response.definition);
                setIsOrigin(definition.template === '__origin__');
                setTemplate(definition.template);
                setFormObject({ ...response.object, definition }, true);
            }
        },
        [id, organizationId]
    );

    const [{ organization: scopeOrga }] = useAppContext();

    // We need this to retrieve orga info of the formatter when we are not scoped
    // to a specific orga; we don't trigger the request when we are scoped or when
    // we are in the creation page.
    const [formatterOrganization] = Organization.useApiModel(
        {
            id: formatter?.organization_id,
            cache: false,
            allow_cache: false,
            launch: !organizationId && !!formatter?.organization_id
        },
        [organizationId, formatter?.organization_id]
    );

    const organization = formatterOrganization || (organizationId ? scopeOrga : null);

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query: { search, limit: 50 }
        },
        [search]
    );

    const organizationOptions = React.useMemo(() => {
        if (!organizations || loadingOrganizations || (id && !organization)) {
            return LoadingOptions;
        }

        // when the user is in the scope of a specific orga, list only this orga
        if (organizationId) return [organization.toOption()];

        // otherwise allow to list all orga
        let defaultOptions = organizations.map(org => org.toOption());

        //  if we are in edit page, add the formatter orga to the options list
        return formatterOrganization ? defaultOptions.concat(organization.toOption()) : defaultOptions;
    }, [organization, organizations, loadingOrganizations, LoadingOptions, id]);

    const backLink = React.useMemo(() => {
        if (!organizationId) {
            return '/tools/formatters';
        }
        return `/organizations/O${organizationId}/tools/formatters`;
    }, [organizationId]);

    const valuesDatas = React.useMemo(() => {
        if (!form.definition?.jmespath_values || (id && loadingFormatter)) {
            return [];
        }
        return Object.entries(form.definition?.jmespath_values).map(([name, value], index) => ({
            name,
            value,
            key: index,
            disabled: !canManageFormatters
        }));
    }, [form.definition, loadingFormatter, user, id]);

    const errors = React.useMemo(() => {
        return {
            filters: Boolean(form.definition?.jmespath_filter && !validateJmesPath(form.definition?.jmespath_filter)),
            values: valuesDatas?.length ? valuesDatas.filter(item => item.name && item.value && !validateJmesPath(item.value)) : []
        };
    }, [form.definition, valuesDatas, validateJmesPath]);

    const disabled = React.useMemo(() => {
        return !canManageFormatters || errors.filters || !!errors.values.length || !form.name || !form.organization_id;
    }, [form, user, errors]);

    const handleSave = React.useCallback(() => {
        const action = id ? 'update' : 'create';
        const adaptedForm = {
            ...form,
            definition: JSON.stringify({
                ...form.definition,
                jmespath_values: filterEmptyValues({ ...form.definition?.jmespath_values }),
                template: isOrigin ? '__origin__' : template
            })
        };

        delete adaptedForm.is_linked_to_webhook;

        const newFormatter = new FormatterModel(adaptedForm);
        newFormatter
            .save()
            .then(response => {
                if ([200, 201].includes(response.status)) {
                    history.push(backLink);
                    toast.success(t(`sauron.formatter.toast.${action}.success`));
                }
            })
            .catch(() => {
                toast.error(t(`sauron.formatter.toast.${action}.error`));
                history.push(backLink);
            });
    }, [form, form.definition, template, backLink, isOrigin]);

    const handleAction = React.useCallback(
        (_, action) => {
            formatter[action]({ organizationId })
                .then(() => {
                    history.push(backLink);
                    toast.success(t(`sauron.formatter.toast.delete.success`));
                })
                .catch(err => {
                    console.error('Cannot delete formatter', err);
                    toast.error(t(`sauron.formatter.toast.delete.error`));
                });
        },
        [organizationId, formatter, backLink]
    );

    return (
        <>
            <WeezHeader
                rightComponent={[
                    <div key={0}>
                        <Button primary inverted label={t('common.cta.save')} onClick={handleSave} disabled={disabled} />
                        {id && canManageFormatters && (
                            <ButtonDropdown
                                key="dropdown"
                                disabled={!formatter || loadingFormatter}
                                items={<FormatterActions formatter={formatter} handleAction={handleAction} actions={['delete']} />}
                                trigger={<Button key={1} inverted icon={<Icon name={'chevron-bottom'} />} label={t('common.actions')} />}
                            />
                        )}
                    </div>
                ]}
                title={t(`sauron.formatter.${id ? 'edit' : 'create'}.title`)}
                backLink={{
                    onClick: () => history.push(backLink)
                }}
            />
            <FrameCard title={t(`sauron.formatter.edit.parameters.title`)} subtitle={t(`sauron.formatter.edit.parameters.subtitle`)} separator>
                <FormatterSettings
                    form={form}
                    search={search}
                    setForm={setForm}
                    setSearch={setSearch}
                    organization={organization}
                    options={organizationOptions}
                    organizationId={organizationId}
                    disabled={!canManageFormatters}
                    loading={id && loadingFormatter}
                />
            </FrameCard>
            <FrameCard title={t(`sauron.formatter.edit.details.title`)} subtitle={t(`sauron.formatter.edit.details.subtitle`)} separator>
                <FormatterDetails
                    form={form}
                    errors={errors}
                    template={template}
                    isOrigin={isOrigin}
                    values={valuesDatas}
                    setIsOrigin={setIsOrigin}
                    setTemplate={setTemplate}
                    setFormObject={setFormObject}
                    disabled={!canManageFormatters}
                    loading={id && loadingFormatter}
                />
            </FrameCard>
        </>
    );
};

const FormatterSettings = ({ form, setForm, organization, organizationId, search, setSearch, loading, disabled, options }) => {
    const { t } = useTranslation();

    return (
        <LoadingCard loading={(!organizationId && loading && !search) || (organizationId && !organization)}>
            <div className={css['formatter-input']}>
                <InfoTextTooltip tooltip={false} title={t('sauron.formatter.name')} className={css['formatter-input-label']} subtitle={t('sauron.formatter.name-subtitle')} />
                <Input value={form?.name} placeholder={t('sauron.formatter.name_placeholder')} disabled={disabled} onChange={({ target: { value } }) => setForm('name', value)} />
            </div>

            <div className={css['formatter-section-title']}>{t(`sauron.formatter.section_orga`)}</div>
            <Section>
                <SelectHierarchyLabel
                    searchValue={search}
                    options={options}
                    placeholder={t('common.select')}
                    externalOptions={options}
                    label={t('sauron.formatter.organizer')}
                    onExternalSearch={value => setSearch(value)}
                    onClear={() => setForm('organization_id', null)}
                    value={form?.organization_id || organization?.id}
                    onChange={value => {
                        setForm('organization_id', value);
                    }}
                    disabled={organizationId}
                />
            </Section>
        </LoadingCard>
    );
};

const FormatterDetails = ({ form, setFormObject, loading, errors, values, disabled, isOrigin, setIsOrigin, template, setTemplate }) => {
    const { t } = useTranslation();
    return (
        <LoadingCard loading={loading}>
            <FormatterDetailsSection title={t('sauron.formatter.values.label')} subtitle={t(`sauron.formatter.edit.parameters.values`)}>
                <FormatterErrors display={!!errors.values.length} />
                <FormatterValues form={form} setFormObject={setFormObject} values={values} errors={errors.values} disabled={disabled} />
            </FormatterDetailsSection>

            <FormatterDetailsSection title={t('sauron.formatter.filters')} subtitle={t(`sauron.formatter.edit.parameters.filters`)}>
                <FormatterErrors display={errors.filters} />
                <Input
                    disabled={disabled}
                    errors={errors.filters}
                    value={form.definition?.jmespath_filter ?? ''}
                    placeholder={t('sauron.formatter.filters_placeholder')}
                    onChange={({ target: { value } }) => setFormObject({ ...form, definition: { ...form.definition, jmespath_filter: value } })}
                />
            </FormatterDetailsSection>

            <FormatterDetailsSection title={t('sauron.formatter.template')} subtitle={t(`sauron.formatter.edit.parameters.template`)}>
                <CheckboxLabel checked={isOrigin} disabled={disabled} label={t('sauron.formatter.default_template')} onChange={({ target: { checked } }) => setIsOrigin(checked)} />
                <div className={css['formatter-input-template']} datatype={isOrigin || disabled ? 'disabled-json-editor' : 'false'}>
                    <JsonEditorField loading={loading} values={isOrigin ? '__origin__' : template} handleChange={values => setTemplate(values)} />
                </div>
            </FormatterDetailsSection>
        </LoadingCard>
    );
};

const FormatterDetailsSection = ({ title = '', subtitle = '', children }) => {
    return (
        <Accordion openByDefault title={<InfoTextTooltip tooltip={false} title={title} subtitle={subtitle} />} disableToggle>
            {children}
        </Accordion>
    );
};

const FormatterErrors = ({ display }) => {
    const { t } = useTranslation();

    if (!display) {
        return null;
    }

    return (
        <Message title={t('sauron.formatter.values.error_message.title')} type="danger">
            <div>{t('sauron.formatter.values.error_message.content')}</div>
            <div>{t('sauron.formatter.values.error_message.sub_content')}</div>
        </Message>
    );
};
